import React, { useCallback, useEffect, useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  MedtechUserGroup,
  TemplateDetail,
  UpdateTemplateProperties,
  UpdateTemplatePropertiesSchema,
} from '../../types/templateDetail';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  QButton,
  QFormControl,
  QInput,
  QModal,
  QModalActions,
  QModalBody,
  QModalHeader,
  QMultiSelect,
  QRadio,
  QRadioGroup,
  QSelect,
  QStack,
  QText,
  useFeatureFlag,
  useToastProvider,
} from '@qualio/ui-components';
import {
  fileDownloadOptions,
  fileDownloadUserGroupsMap,
  resolveFileDownloadUserGroups,
  reviewOptions,
} from '../../utils/templateFormUtil';
import { useUpdateTemplateDraft } from '../../hooks/useUpdateTemplateDraft';
import { filterTagOption, useTagOptions } from '../../hooks/useTagOptions';
import { extractMessageFromError, isPrefixInUseByParentError } from '../../utils/backEndErrorMessages';

type PropertiesModalProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  data: TemplateDetail;
};
const PropertiesModal: React.FC<PropertiesModalProps> = ({ isOpen, setIsOpen, data }) => {
  const { t } = useTranslation();
  const { showToast } = useToastProvider();

  const { mutate: updateTemplateDraftMutate, isLoading: isSaving } = useUpdateTemplateDraft(data.template_uuid);
  const templateDefaultTags = Boolean(useFeatureFlag()['template-default-tags']);
  const { options: tagOptions, isLoading: isLoadingTags } = useTagOptions();
  const formMethods = useForm<UpdateTemplateProperties>({
    mode: 'onSubmit',
    resolver: zodResolver(UpdateTemplatePropertiesSchema),
  });
  const {
    handleSubmit,
    control,
    setError,
    reset,
    formState: { errors },
  } = formMethods;
  const formHasErrors = !!Object.keys(errors).length;
  const formValues = useMemo<UpdateTemplateProperties>(
    () => ({
      name: data.name,
      prefix: data.prefix.replace(/-*$/, ''),
      training_available: data.training_available,
      training_required: data.training_required,
      document_review_period: data.document_review_period ?? 0,
      document_review_due_in_days: data.document_review_due_in_days,
      document_approval_due_in_days: data.document_approval_due_in_days,
      file_download_user_groups: resolveFileDownloadUserGroups(data.file_download_user_groups),
      document_effective_on_approval: data.document_effective_on_approval,
      document_text_sections_visible: data.document_text_sections_visible,
      tag_ids: data.tag_ids ?? [],
    }),
    [data],
  );
  const onSubmit = useCallback(
    (updated_data: UpdateTemplateProperties) => {
      const payload = {
        ...updated_data,
        type: updated_data.name,
        company_id: data.company_id,
        sections: data.sections,
        resolutions: [],
        document_review_period: updated_data.document_review_period === 0 ? null : updated_data.document_review_period,
        file_download_user_groups: fileDownloadUserGroupsMap[
          updated_data.file_download_user_groups
        ] as MedtechUserGroup[],
      };
      updateTemplateDraftMutate(payload, {
        onSuccess: () => {
          setIsOpen(false);
        },
        onError: (error: any) => {
          const message = extractMessageFromError(error);
          if (isPrefixInUseByParentError(error)) {
            setError('prefix', {
              type: 'server',
              message: t('template.properties.errors.prefixInUseByParent'),
            });
          } else if (message.includes('Template prefix is already in use')) {
            setError('prefix', {
              type: 'server',
              message: t('template.properties.errors.prefixInUse'),
            });
          } else if (message.includes('Template title is already in use')) {
            setError('name', {
              type: 'server',
              message: t('template.properties.errors.titleInUse'),
            });
          } else {
            showToast({
              description: extractMessageFromError(error),
              status: 'error',
              title: t('common.toast.error'),
              'data-cy': 'toast-properties-save-error',
            });
          }
        },
      });
    },
    [setIsOpen, data, setError, updateTemplateDraftMutate, showToast, t],
  );
  const handleClose = useCallback(() => {
    setIsOpen(false);
    reset(formValues);
  }, [setIsOpen, reset, formValues]);
  useEffect(() => {
    reset(formValues);
  }, [formValues, reset]);

  return (
    <QModal isOpen={isOpen} onClose={handleClose} size="lg">
      <QModalHeader>
        <QText>{t('template.properties.modalTitle')}</QText>
      </QModalHeader>
      <QModalBody>
        <QStack direction="column" color="gray.700" spacing="30px">
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <QStack direction="column" spacing="30px">
                <QFormControl
                  label={t('template.properties.fields.title')}
                  error={errors.name && errors.name.message}
                  isInvalid={!!errors.name}
                  data-cy="template-name-input"
                  isRequired
                >
                  <Controller
                    control={control}
                    name="name"
                    render={({ field: { ref: _ref, ...field } }) => <QInput {...field} value={field.value} />}
                  />
                </QFormControl>
                <QFormControl
                  label={t('template.properties.fields.prefix')}
                  error={errors.prefix && errors.prefix.message}
                  isInvalid={!!errors.prefix}
                  data-cy="template-prefix-input"
                  isRequired
                >
                  <Controller
                    control={control}
                    name="prefix"
                    render={({ field: { ref: _ref, ...field } }) => <QInput {...field} value={field.value} />}
                  />
                </QFormControl>
                <QFormControl
                  label={t('template.properties.fields.eligibleForTraining')}
                  error={errors.training_available && errors.training_available.message}
                  isInvalid={!!errors.training_available}
                  data-cy="template-training-available-input"
                >
                  <Controller
                    control={control}
                    name="training_available"
                    render={({ field: { ref: _ref, onChange, ...field } }) => (
                      <QRadioGroup
                        {...field}
                        onChange={(val) => onChange(val === '1' ? true : false)}
                        value={field.value ? '1' : '2'}
                      >
                        <QStack direction="column">
                          <QRadio value="1">{t('common.boolean.yes')}</QRadio>
                          <QRadio value="2">{t('common.boolean.no')}</QRadio>
                        </QStack>
                      </QRadioGroup>
                    )}
                  />
                </QFormControl>
                <QFormControl
                  label={t('template.properties.fields.requireTrainingEachVersion')}
                  error={errors.training_required && errors.training_required.message}
                  isInvalid={!!errors.training_required}
                  data-cy="template-training-required-input"
                >
                  <Controller
                    control={control}
                    name="training_required"
                    render={({ field: { ref: _ref, onChange, ...field } }) => (
                      <QRadioGroup
                        {...field}
                        onChange={(val) => onChange(val === '1' ? true : false)}
                        value={field.value === true ? '1' : '2'}
                      >
                        <QStack direction="column">
                          <QRadio value="1">{t('common.boolean.yes')}</QRadio>
                          <QRadio value="2">{t('common.boolean.no')}</QRadio>
                        </QStack>
                      </QRadioGroup>
                    )}
                  />
                </QFormControl>
                <QFormControl
                  label={t('template.properties.fields.periodicReviewPeriod')}
                  error={errors.document_review_period && errors.document_review_period.message}
                  isInvalid={!!errors.document_review_period}
                  data-cy="template-document-review-period-input"
                >
                  <Controller
                    control={control}
                    name="document_review_period"
                    render={({ field: { ref: _ref, onChange, ...field } }) => (
                      <QSelect
                        {...field}
                        onChange={(changeObject) => onChange(changeObject?.value)}
                        options={reviewOptions}
                        menuPosition="fixed"
                      />
                    )}
                  />
                </QFormControl>
                <QFormControl
                  label={t('template.properties.fields.reviewLimit')}
                  error={errors.document_review_due_in_days && errors.document_review_due_in_days.message}
                  isInvalid={!!errors.document_review_due_in_days}
                  data-cy="template-document-review-due-in-days-input"
                >
                  <Controller
                    control={control}
                    name="document_review_due_in_days"
                    render={({ field: { ref: _ref, onChange, ...field } }) => (
                      <QInput
                        {...field}
                        type="number"
                        onChange={(e) => onChange(e.target.value === '' ? 0 : e.target.valueAsNumber)}
                        value={field.value ? Number(field.value) : undefined}
                        placeholder={t('template.properties.placeholders.none')}
                      />
                    )}
                  />
                </QFormControl>
                <QFormControl
                  label={t('template.properties.fields.approvalLimit')}
                  error={errors.document_approval_due_in_days && errors.document_approval_due_in_days.message}
                  isInvalid={!!errors.document_approval_due_in_days}
                  data-cy="template-document-approval-due-in-days-input"
                >
                  <Controller
                    control={control}
                    name="document_approval_due_in_days"
                    render={({ field: { ref: _ref, onChange, ...field } }) => (
                      <QInput
                        {...field}
                        type="number"
                        onChange={(e) => onChange(e.target.value === '' ? 0 : e.target.valueAsNumber)}
                        value={field.value ? Number(field.value) : undefined}
                        placeholder={t('template.properties.placeholders.none')}
                      />
                    )}
                  />
                </QFormControl>
                <QFormControl
                  label={t('template.properties.fields.downloadPermissions')}
                  error={errors.file_download_user_groups && errors.file_download_user_groups.message}
                  isInvalid={!!errors.file_download_user_groups}
                  data-cy="template-file-download-user-groups-input"
                >
                  <Controller
                    control={control}
                    name="file_download_user_groups"
                    render={({ field: { ref: _ref, onChange, ...field } }) => (
                      <QSelect
                        {...field}
                        onChange={(changeObject) => {
                          if (changeObject) {
                            onChange(changeObject.value);
                          }
                        }}
                        options={fileDownloadOptions}
                      />
                    )}
                  />
                </QFormControl>
                <QFormControl
                  label={t('template.properties.fields.effectiveOnApproval')}
                  error={errors.document_effective_on_approval && errors.document_effective_on_approval.message}
                  isInvalid={!!errors.document_effective_on_approval}
                  data-cy="template-document-effective-on-approval-input"
                >
                  <Controller
                    control={control}
                    name="document_effective_on_approval"
                    render={({ field: { ref: _ref, onChange, ...field } }) => (
                      <QRadioGroup
                        {...field}
                        onChange={(val) => onChange(val === '1' ? true : false)}
                        value={field.value ? '1' : '2'}
                      >
                        <QStack direction="column">
                          <QRadio value="1">{t('common.boolean.yes')}</QRadio>
                          <QRadio value="2">{t('common.boolean.no')}</QRadio>
                        </QStack>
                      </QRadioGroup>
                    )}
                  />
                </QFormControl>
                <QFormControl
                  label={t('template.properties.fields.showSectionTitles')}
                  error={errors.document_text_sections_visible && errors.document_text_sections_visible.message}
                  isInvalid={!!errors.document_text_sections_visible}
                  data-cy="template-document-text-sections-visible-input"
                >
                  <Controller
                    control={control}
                    name="document_text_sections_visible"
                    render={({ field: { ref: _ref, onChange, ...field } }) => (
                      <QRadioGroup
                        {...field}
                        onChange={(val) => onChange(val === '1' ? true : false)}
                        value={field.value === true ? '1' : '2'}
                      >
                        <QStack direction="column">
                          <QRadio value="1">{t('common.boolean.yes')}</QRadio>
                          <QRadio value="2">{t('common.boolean.no')}</QRadio>
                        </QStack>
                      </QRadioGroup>
                    )}
                  />
                </QFormControl>
                {templateDefaultTags && (
                  <QFormControl
                    label={t('template.properties.fields.defaultTags')}
                    error={errors.tag_ids?.message}
                    isInvalid={!!errors.tag_ids}
                    data-cy="template-default-tags-input"
                    helper={t('template.properties.helpers.defaultTags')}
                  >
                    <Controller
                      control={control}
                      name="tag_ids"
                      render={({ field: { ref: _ref, onChange, ...field } }) => (
                        <QMultiSelect
                          {...field}
                          size="sm"
                          onChange={(selected) => onChange(selected.map((option) => option.value))}
                          options={tagOptions}
                          filterOption={filterTagOption}
                          isLoading={isLoadingTags}
                          isClearable
                          menuPosition="fixed"
                          aria-label={t('template.properties.fields.defaultTags')}
                        />
                      )}
                    />
                  </QFormControl>
                )}
              </QStack>
            </form>
          </FormProvider>
        </QStack>
      </QModalBody>
      <QModalActions>
        <QButton onClick={handleClose} variant="outline" data-cy="edit-properties-cancel-button">
          {t('common.buttons.cancel')}
        </QButton>
        <QButton
          onClick={handleSubmit(onSubmit)}
          isDisabled={formHasErrors || isSaving}
          isLoading={isSaving}
          data-cy="edit-properties-button"
        >
          {t('common.buttons.save')}
        </QButton>
      </QModalActions>
    </QModal>
  );
};

export default PropertiesModal;
